import { useEffect, useRef, useState } from 'react';
import { svDest } from '../lib/svgeo';
import { toast } from 'sonner';
import { MapPin, X, Camera, Save, ChevronLeft, Video } from 'lucide-react';
import { videoThumb } from '../lib/media';
import { WP_KINDS } from '../lib/track';
import { feat } from '../lib/features';
import { getMode } from '../lib/profiles';
import { useTrackRec, curPos, addWaypoint } from '../lib/trackRec';
import { altitudeState, fmtAlt } from '../lib/altitude';
import { compressImage, uid } from '../lib/model';
import { VoiceRecorder } from './VoiceMemo';
import WoodFields from './WoodFields';
import { woodData } from '../lib/woodList';

// floating "point on route" button + one-hand quick menu during Follow-Me recording
export default function WaypointFab({ t, onPoint, mapApi }) {
  const rec = useTrackRec();
  const [kind, setKind] = useState(null);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const camRef = useRef(null);
  const vidRef = useRef(null);
  const [pj, setPj] = useState(null);
  useEffect(() => { const on = () => { setOpen(true); setPj({ src: 'cross', brg: '', dist: '' }); }; window.addEventListener('giss-proj-open', on); return () => window.removeEventListener('giss-proj-open', on); }, []);
  const pjStart = () => {
    if (!pj) return null;
    if (pj.src === 'gps') { const p = curPos(); return p ? [p.lat, p.lon] : null; }
    if (pj.src === 'cross') { const c = mapApi.current.getCenter(); return [c.lat, c.lng]; }
    const w = (rec?.wps || []).find((x) => x.id === pj.src); return w ? [w.lat, w.lon] : null;
  };
  const pjTarget = () => { const s = pjStart(), b = +String(pj?.brg).replace(',', '.'), d = +String(pj?.dist).replace(',', '.'); return s && pj.brg !== '' && b >= 0 && b <= 360 && d > 0 ? svDest(s, b, d) : null; };
  const tgt = pj && pjTarget();
  useEffect(() => { mapApi?.current?.svyMarks(pj ? [pjStart(), tgt].filter(Boolean) : []); }, [pj, tgt?.[0], tgt?.[1]]); // eslint-disable-line react-hooks/exhaustive-deps
  const live = rec && rec.status !== 'done';
  const md = getMode(), forest = md === 'garden' || md === 'all';
  if (!live && !pj) return null;
  const pjDone = () => {
    if (!tgt) return toast.error(t.pjNeed);
    if (!live) { mapApi.current.setView(tgt[0], tgt[1], 20); toast.success(t.pjCentered); setPj(null); setOpen(false); return; }
    setF((x) => ({ ...x, at: { lat: tgt[0], lon: tgt[1], alt: null, acc: null } })); setPj(null);
  };
  const pos = f.at || curPos();
  const no = (rec?.wps || []).length + 1;
  const reset = () => { setKind(null); setOpen(false); setF({}); setPj(null); };
  const pick = (k) => { setKind(k); setF((x) => ({ at: x.at, status: WP_KINDS[k].st[0] || null, note: '', media: [], memos: [], data: {} })); };
  const save = () => {
    const p = f.at || curPos();
    if (!p) return toast.error(t.wpNoGps);
    const a = altitudeState();
    const alt = p.alt ?? a.alt ?? null;
    const ref = `R${rec.no || 1}-P${no}`;
    const data = kind === 'woodPile' ? woodData(f.data) : f.data;
    const wp = { id: uid('wp'), kind, status: f.status, note: f.note.trim(), lat: p.lat, lon: p.lon, alt, acc: p.acc ?? null, t: Date.now(), photos: f.media.length, memos: f.memos.length, ...(Object.keys(data || {}).length ? { data } : {}) };
    wp.videos = f.media.filter((m) => m.kind === 'video').length;
    wp.photos -= wp.videos;
    const fid = onPoint({ ...wp, ref, media: f.media, memos: f.memos, altSrc: p.alt != null ? 'gps' : a.src });
    if (fid) wp.fid = fid;
    addWaypoint(wp);
    toast.success(`📍 ${ref} · ${t['wpK_' + kind]}`);
    reset();
  };
  return (
    <>
      {!open && rec?.status === 'rec' && <button className="wp-fab" data-testid="wp-fab-btn" onClick={() => setOpen(true)}><MapPin size={22} /><span>{t.wpFab}</span></button>}
      {open && (
        <div className="wp-sheet" data-testid="wp-sheet">
          <div className="wp-head">
            {kind && <button className="icon-btn" data-testid="wp-back-btn" onClick={() => setKind(null)}><ChevronLeft size={18} /></button>}
            <b>{live ? `📍 R${rec.no || 1}-P${no}` : `🧭 ${t.pjTitle}`}</b>
            <small data-testid="wp-pos">{pos ? `${pos.lat.toFixed(6)}, ${pos.lon.toFixed(6)} · ${fmtAlt(pos.alt ?? altitudeState().alt)} ${t.altAsl}${pos.acc ? ` · ±${Math.round(pos.acc)} m` : ''}` : t.wpNoGps}</small>
            <button className="icon-btn" data-testid="wp-close-btn" onClick={reset}><X size={18} /></button>
          </div>
          {pj && (
            <div className="wp-proj" data-testid="wp-proj">
              <select data-testid="wp-proj-src" value={pj.src} onChange={(e) => setPj({ ...pj, src: e.target.value })}>
                <option value="cross">{t.pjSrcCross}</option>
                <option value="gps">{t.pjSrcGps}</option>
                {(rec?.wps || []).map((w) => <option key={w.id} value={w.id}>R{rec.no || 1}-P{w.no} {t['wpK_' + w.kind]}</option>)}
              </select>
              <label>{t.pjBrg} (0–360°)<input type="text" inputMode="decimal" data-testid="wp-proj-brg" value={pj.brg} onChange={(e) => setPj({ ...pj, brg: e.target.value })} /></label>
              <label>{t.pjDist} (m)<input type="text" inputMode="decimal" data-testid="wp-proj-dist" value={pj.dist} onChange={(e) => setPj({ ...pj, dist: e.target.value })} /></label>
              <small data-testid="wp-proj-target">{tgt ? `🎯 ${tgt[0].toFixed(6)}, ${tgt[1].toFixed(6)}` : t.pjNeed}</small>
              <button className="btn btn-green wp-save" data-testid="wp-proj-confirm-btn" disabled={!tgt} onClick={pjDone}>🎯 {t.pjConfirm}</button>
            </div>
          )}
          {!kind && !pj && (
            <div className="wp-kinds">
              <button className="wp-kind" style={{ '--c': '#0f172a' }} data-testid="wp-kind-proj" onClick={() => setPj({ src: 'gps', brg: '', dist: '' })}><span>🧭</span>{t.pjTitle}</button>
              {Object.entries(WP_KINDS).filter(([k]) => ({ fit: feat('fittings') && md !== 'outdoor' && md !== 'garden', gas: feat('gas') && md !== 'outdoor', leak: feat('leak') && md !== 'outdoor', woodPile: forest, tree: forest, damage: forest }[k] ?? true)).map(([k, c]) => (
                <button key={k} className="wp-kind" style={{ '--c': c.color }} data-testid={`wp-kind-${k}`} onClick={() => pick(k)}><span>{c.icon}</span>{t['wpK_' + k]}</button>
              ))}
            </div>
          )}
          {kind && (
            <>
              {WP_KINDS[kind].st.length > 0 && (
                <div className="wp-st">
                  {WP_KINDS[kind].st.map((s) => <button key={s} className={`chip seg ${f.status === s ? 'on' : ''}`} data-testid={`wp-status-${s}`} onClick={() => setF({ ...f, status: s })}>{t['wpS_' + s]}</button>)}
                </div>
              )}
              {(
                <div className="wp-st">
                  <button className="btn btn-outline" data-testid="wp-photo-btn" onClick={() => camRef.current.click()}><Camera size={17} /> {t.wpPhoto}{f.media.some((m) => m.kind === 'photo') ? ` (${f.media.filter((m) => m.kind === 'photo').length})` : ''}</button>
                  <button className="btn btn-outline" data-testid="wp-video-btn" onClick={() => vidRef.current.click()}><Video size={17} /> {t.wpVideo}{f.media.some((m) => m.kind === 'video') ? ` (${f.media.filter((m) => m.kind === 'video').length})` : ''}</button>
                  <input ref={vidRef} type="file" accept="video/*" capture="environment" hidden data-testid="wp-video-input" onChange={async (e) => { const file = e.target.files?.[0]; e.target.value = ''; if (file) { const th = await videoThumb(file).catch(() => ({})); setF((x) => ({ ...x, media: [...x.media, { id: uid('ph'), kind: 'video', blob: file, mime: file.type, caption: '', createdAt: Date.now(), ...th }] })); } }} />
                  <input ref={camRef} type="file" accept="image/*" capture="environment" hidden data-testid="wp-photo-input" onChange={async (e) => { const file = e.target.files?.[0]; e.target.value = ''; if (file) { const img = await compressImage(file); setF((x) => ({ ...x, media: [...x.media, { id: uid('ph'), kind: 'photo', createdAt: Date.now(), ...img }] })); } }} />
                  <VoiceRecorder t={t} compact testid="wp-voice" onDone={(m) => setF((x) => ({ ...x, memos: [...x.memos, m] }))} />
                  {f.memos.length > 0 && <small data-testid="wp-memo-count">🎙️ {f.memos.length}</small>}
                </div>
              )}
              {kind === 'woodPile' && <WoodFields t={t} data={f.data} set={(data) => setF({ ...f, data })} />}
              {kind !== 'woodPile' && WP_KINDS[kind].fields && (
                <div className="wp-st wp-fields" data-testid="wp-fields">
                  {Object.entries(WP_KINDS[kind].fields).map(([k, ty]) => (ty === 'check'
                    ? <label key={k} className="wp-chk"><input type="checkbox" data-testid={`wp-field-${k}`} checked={!!f.data[k]} onChange={(e) => setF({ ...f, data: { ...f.data, [k]: e.target.checked } })} /> {t['wpF_' + k]}</label>
                    : <label key={k}>{t['wpF_' + k]}<input data-testid={`wp-field-${k}`} inputMode={ty === 'num' ? 'decimal' : 'text'} value={f.data[k] || ''} onChange={(e) => setF({ ...f, data: { ...f.data, [k]: e.target.value } })} /></label>))}
                </div>
              )}
              <input className="wp-note" data-testid="wp-note-input" placeholder={t.wpNote} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
              <button className="btn btn-green wp-save" style={{ background: WP_KINDS[kind].color }} data-testid="wp-save-btn" onClick={save}><Save size={18} /> {t.wpSave}</button>
            </>
          )}
        </div>
      )}
    </>
  );
}
